const COOKIE = 'cs_analytics_excluded';
const HOSTS = new Set([
  'callsyncapp.com',
  'www.callsyncapp.com',
  'dash.callsyncapp.com',
  'help.callsyncapp.com',
  'sales.callsyncapp.com',
]);

function supported(win: Window) {
  return win.location.protocol === 'https:' && HOSTS.has(win.location.hostname);
}
export function persistBrowserExclusion(win: Window, doc: Document, excluded: boolean) {
  if (!supported(win)) return;
  try {
    doc.cookie = `${COOKIE}=${excluded ? '1' : '0'}; Domain=callsyncapp.com; Path=/; Max-Age=31536000; Secure; SameSite=Lax`;
  } catch {
    /* IP check still applies if cookies are unavailable. */
  }
  try {
    if (excluded) win.localStorage.setItem(COOKIE, '1');
    else win.localStorage.removeItem(COOKIE);
  } catch {
    /* Cross-subdomain cookie is the primary browser preference. */
  }
}
export function browserTrafficExcluded(win: Window, doc: Document): boolean {
  if (!supported(win)) return false;
  const url = new URL(win.location.href);
  const preference = url.searchParams.get('analytics');
  if (preference === 'off' || preference === 'on') {
    persistBrowserExclusion(win, doc, preference === 'off');
    url.searchParams.delete('analytics');
    try {
      win.history.replaceState(win.history.state, '', url.href);
    } catch {
      /* Preference still applies. */
    }
    return preference === 'off';
  }
  try {
    const preference = doc.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${COOKIE}=`));
    if (preference !== undefined) return preference === `${COOKIE}=1`;
  } catch {
    /* Try local storage. */
  }
  try {
    return win.localStorage.getItem(COOKIE) === '1';
  } catch {
    return false;
  }
}

const CHECKED = 'cs_analytics_checked';
// A clean IP check is shared with the other subdomains for 30 minutes, so the signup page opened from the website
// tracks at once instead of waiting for a second check on a slow phone.
const CHECKED_SECONDS = 30 * 60;

export function rememberTrafficCheck(win: Window, doc: Document) {
  if (!supported(win)) return;
  try {
    doc.cookie = `${CHECKED}=1; Domain=callsyncapp.com; Path=/; Max-Age=${CHECKED_SECONDS}; Secure; SameSite=Lax`;
  } catch {
    /* The next page runs the check itself. */
  }
}
export function trafficCheckedRecently(win: Window, doc: Document): boolean {
  if (!supported(win)) return false;
  try {
    return doc.cookie.split(';').some((part) => part.trim() === `${CHECKED}=1`);
  } catch {
    return false;
  }
}

/** No raw IP is returned to the browser. The edge compares its trusted connection header. */
export async function visitorTrafficExcluded(win: Window, doc: Document): Promise<boolean> {
  const response = await win.fetch('https://callsyncapp.com/api/analytics-traffic', {
    cache: 'no-store',
    credentials: 'omit',
    mode: 'cors',
    referrerPolicy: 'no-referrer',
    // Slow mobile connections often need more than 3 s; until it answers, events wait in the queue.
    signal: AbortSignal.timeout(8000),
  });
  if (!response.ok) throw new Error('Analytics traffic check unavailable');
  const data = (await response.json()) as { excluded?: unknown };
  if (typeof data.excluded !== 'boolean') throw new Error('Invalid analytics traffic response');
  if (data.excluded) persistBrowserExclusion(win, doc, true);
  else rememberTrafficCheck(win, doc);
  return data.excluded;
}

const TEST_COOKIE = 'cs_meta_test';
const TEST_CODE = /^TEST[A-Za-z0-9]{1,40}$/;
/**
 * `?meta_test=TEST…` (the code from Meta Events Manager → Test events) marks this browser for two
 * hours on every callsyncapp.com subdomain: trackers load even for excluded internal traffic, GA4 uses
 * DebugView, and the dashboard server sends Conversions API events as Test Events only.
 * `?meta_test=off` ends it.
 */
export function metaTestEventCode(win: Window, doc: Document): string | null {
  if (!supported(win)) return null;
  const url = new URL(win.location.href);
  const requested = url.searchParams.get('meta_test');
  if (requested !== null) {
    const code = TEST_CODE.test(requested) ? requested : null;
    try {
      doc.cookie = `${TEST_COOKIE}=${code ?? ''}; Domain=callsyncapp.com; Path=/; Max-Age=${code ? 7200 : 0}; Secure; SameSite=Lax`;
    } catch {
      /* Test mode is optional. */
    }
    url.searchParams.delete('meta_test');
    try {
      win.history.replaceState(win.history.state, '', url.href);
    } catch {
      /* The code is not sensitive. */
    }
    return code;
  }
  try {
    const saved = doc.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${TEST_COOKIE}=`))
      ?.slice(TEST_COOKIE.length + 1);
    return saved && TEST_CODE.test(saved) ? saved : null;
  } catch {
    return null;
  }
}
